import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import axios from 'axios'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { AnimatePresence, motion } from 'motion/react'
import {
  getCandidateProfile,
  saveCandidateProfile,
  uploadProfileResume,
  type ExperienceEntry,
  type EducationEntry,
} from '@/api/client'
import { AppShell } from '@/components/layout/AppShell'
import { ResumeDropzone } from '@/components/ResumeDropzone'
import { useAuth } from '@/contexts/AuthContext'
import { apiErrorMessage } from '@/api/client'
import { cleanSkills, mergeParsedIntoProfile, parsedHasContent, isBlankExperience, isBlankEducation, type ProfileFormState } from '@/lib/profile-utils'
import { EASE_OUT } from '@/lib/ease'
import SkillsPicker from '@/components/profile/skills-picker'
import { TextField, TextAreaField } from '@/components/form/fields'
import { LoadingButton } from '@/components/spectrumui/loading-button'
import { Toaster } from '@/components/ui/toaster'
import { useToast } from '@/hooks/use-toast'
import { usePageMeta } from '@/hooks/use-page-meta';

const MAX_SKILLS = 30

// Client-side per-row identity for the enter/exit rows. The API carries none
// — entries are plain field objects on both the response and the save — so
// identity is assigned at the boundaries and stripped at the save, and
// profile-utils stays API-shaped.
type ExperienceRow = ExperienceEntry & { id: number }
type EducationRow = EducationEntry & { id: number }
type ProfileRowsState = Omit<ProfileFormState, 'experience' | 'education'> & {
  experience: ExperienceRow[]
  education: EducationRow[]
}
let rowIdCounter = 0
const nextRowId = () => ++rowIdCounter
// Rows carried through from the wrapped form keep their runtime ids
// (ProfileFormState's declared shape doesn't see them); rows that enter at a
// boundary — load, parsed merge — get fresh ones. The save echo is the one
// boundary where identity survives: see zipExperienceEcho/zipEducationEcho.
const ensureRowIds = (form: ProfileFormState): ProfileRowsState => ({
  ...form,
  experience: form.experience.map(e => ('id' in e ? (e as ExperienceRow) : { ...e, id: nextRowId() })),
  education: form.education.map(e => ('id' in e ? (e as EducationRow) : { ...e, id: nextRowId() })),
})
// The save echo returns exactly the rows the payload sent, in order (the
// payload kept the non-blank rows in order), so the echo's values zip onto
// those rows with their ids intact — the DOM survives the save instead of
// the whole list crossfading through a fresh-id rehydrate. Spelled out per
// shape like the payload mapping, so an echo field can't silently leak the
// ids (or omit itself); a row beyond the echo's length (server dropped or
// reordered — not expected) falls back to a fresh id.
const zipExperienceEcho = (rows: ExperienceRow[], echo: ExperienceEntry[]): ExperienceRow[] =>
  echo.map((entry, i) => ({
    title: entry.title,
    company: entry.company,
    start: entry.start,
    end: entry.end,
    description: entry.description,
    id: rows[i]?.id ?? nextRowId(),
  }))
const zipEducationEcho = (rows: EducationRow[], echo: EducationEntry[]): EducationRow[] =>
  echo.map((entry, i) => ({
    school: entry.school,
    degree: entry.degree,
    field: entry.field,
    start: entry.start,
    end: entry.end,
    id: rows[i]?.id ?? nextRowId(),
  }))

// The profile renders one section at a time (nav rail + pane, like a settings
// page) so the whole form fits a viewport instead of stacking every card.
// Icons follow the shell's stroke style (AppShell ICONS).
type ProfileSectionId = 'identity' | 'skills' | 'experience' | 'education' | 'resume'
const SECTIONS: { id: ProfileSectionId; label: string; icon: ReactNode }[] = [
  { id: 'identity', label: 'Personal info', icon: <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg> },
  { id: 'skills', label: 'Skills', icon: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z"/></svg> },
  { id: 'experience', label: 'Experience', icon: <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg> },
  { id: 'education', label: 'Education', icon: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 9l10-5 10 5-10 5z"/><path d="M6 11.5V16c0 1.7 2.7 3 6 3s6-1.3 6-3v-4.5"/></svg> },
  { id: 'resume', label: 'Resume', icon: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M16 13H8"/><path d="M16 17H8"/></svg> },
]

export function CandidateProfile() {
  usePageMeta("Candidate profile — Aura")
  const { user } = useAuth()
  const navigate = useNavigate()
  const { toast } = useToast()

  const [form, setForm] = useState<ProfileRowsState | null>(null)
  // Sectioned layout: the nav picks which slice of the form renders. The
  // active section deep-links via ?section= so a reload lands back on the
  // same pane.
  const [searchParams, setSearchParams] = useSearchParams()
  const initialSection = searchParams.get('section')
  const [section, setSection] = useState<ProfileSectionId>(
    SECTIONS.some(s => s.id === initialSection) ? (initialSection as ProfileSectionId) : 'identity',
  )
  // Last server-known state; the per-section unsaved dots compare the live
  // form against it. (Resume is never dirty — its dropzone uploads
  // immediately and the PDF isn't part of this form's save.)
  const [savedSnapshot, setSavedSnapshot] = useState<ProfileRowsState | null>(null)
  const [resumeStored, setResumeStored] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [parsing, setParsing] = useState(false)
  const [parseError, setParseError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  // Async continuations (resume parse, save) must not touch state after unmount.
  const aliveRef = useRef(true)
  useEffect(() => () => { aliveRef.current = false }, [])

  useEffect(() => {
    let cancelled = false
    getCandidateProfile()
      .then(p => {
        if (cancelled) return
        const loaded = ensureRowIds({
          headline: p.headline,
          location: p.location,
          summary: p.summary,
          skills: p.skills,
          experience: p.experience,
          education: p.education,
          linkedin_url: p.linkedin_url,
          github_url: p.github_url,
          portfolio_url: p.portfolio_url,
        })
        setForm(loaded)
        setSavedSnapshot(loaded)
        setResumeStored(p.resume_stored)
      })
      .catch(err => {
        if (cancelled) return
        if (err?.response?.status === 401) navigate('/', { replace: true })
        setParseError('Failed to load your profile.')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [navigate])

  if (loading) {
    return (
      <AppShell>
        <div className="wloading" role="status" aria-live="polite">
          <span className="wspinner" aria-hidden="true"></span>
          <span>Loading profile…</span>
        </div>
      </AppShell>
    )
  }

  if (!form) {
    return (
      <AppShell>
        <div className="wbanner" role="alert">{parseError ?? 'Profile unavailable.'}</div>
      </AppShell>
    )
  }

  const set = <K extends keyof ProfileRowsState>(key: K, value: ProfileRowsState[K]) =>
    setForm(f => (f ? { ...f, [key]: value } : f))

  const showSection = (id: ProfileSectionId) => {
    setSection(id)
    setSearchParams(id === 'identity' ? {} : { section: id }, { replace: true })
  }

  // The exact API-payload mapping submit() has always sent — shared with the
  // dirty dots so a section is flagged precisely when its slice of the next
  // save would differ from the server-known state.
  const payloadOf = (f: ProfileRowsState) => ({
    headline: f.headline.trim(),
    location: f.location.trim(),
    summary: f.summary.trim(),
    skills: f.skills,
    experience: f.experience
      .filter(entry => !isBlankExperience(entry))
      .map(entry => ({ title: entry.title, company: entry.company, start: entry.start, end: entry.end, description: entry.description })),
    education: f.education
      .filter(entry => !isBlankEducation(entry))
      .map(entry => ({ school: entry.school, degree: entry.degree, field: entry.field, start: entry.start, end: entry.end })),
    linkedin_url: f.linkedin_url?.trim() || null,
    github_url: f.github_url?.trim() || null,
    portfolio_url: f.portfolio_url?.trim() || null,
  })

  const isSectionDirty = (id: ProfileSectionId) => {
    if (!savedSnapshot) return false
    const live = payloadOf(form)
    const saved = payloadOf(savedSnapshot)
    switch (id) {
      case 'identity':
        return JSON.stringify([live.headline, live.location, live.summary, live.linkedin_url, live.github_url, live.portfolio_url])
          !== JSON.stringify([saved.headline, saved.location, saved.summary, saved.linkedin_url, saved.github_url, saved.portfolio_url])
      case 'skills': return JSON.stringify(live.skills) !== JSON.stringify(saved.skills)
      case 'experience': return JSON.stringify(live.experience) !== JSON.stringify(saved.experience)
      case 'education': return JSON.stringify(live.education) !== JSON.stringify(saved.education)
      case 'resume': return false
    }
  }

  const addExperience = () =>
    set('experience', [...form.experience, { title: '', company: '', start: '', end: '', description: '', id: nextRowId() }])

  const updateExperience = (id: number, patch: Partial<ExperienceEntry>) =>
    set('experience', form.experience.map(e => (e.id === id ? { ...e, ...patch } : e)))

  const removeExperience = (id: number) =>
    set('experience', form.experience.filter(e => e.id !== id))

  const addEducation = () =>
    set('education', [...form.education, { school: '', degree: '', field: '', start: '', end: '', id: nextRowId() }])

  const updateEducation = (id: number, patch: Partial<EducationEntry>) =>
    set('education', form.education.map(e => (e.id === id ? { ...e, ...patch } : e)))

  const removeEducation = (id: number) =>
    set('education', form.education.filter(e => e.id !== id))

  const handleResume = async (file: File) => {
    setParsing(true)
    setParseError(null)
    try {
      const { parsed, resume_stored } = await uploadProfileResume(file)
      if (!aliveRef.current) return
      setResumeStored(resume_stored)
      if (parsedHasContent(parsed)) {
        setForm(f => (f ? ensureRowIds(mergeParsedIntoProfile(f, parsed)) : f))
        toast(
          resume_stored
            ? {
                title: 'Resume parsed',
                description: 'Saved values were kept; new skills and entries from your resume were added below. Review and save.',
              }
            : {
                // Parse succeeded but MinIO storage failed: the values are
                // prefilled for review, yet nothing powers one-click
                // interviews until a re-upload stores the PDF.
                title: 'Resume parsed, but not stored',
                description: 'Values from your resume were added below, but the PDF could not be stored — upload it again to keep it on file for one-click interviews.',
                variant: 'destructive',
              }
        )
      } else {
        toast(
          resume_stored
            ? {
                title: 'Resume stored',
                description: 'The PDF is on your profile, but nothing could be auto-extracted. Fill the form manually.',
                variant: 'destructive',
              }
            : {
                title: 'Upload failed',
                description: 'The PDF could not be stored, and nothing could be auto-extracted. Please try again.',
                variant: 'destructive',
              }
        )
      }
    } catch (error) {
      if (!aliveRef.current) return
      // Mirror the load path: an expired session during upload redirects home
      // instead of leaving the user on a page whose every action now fails.
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        navigate('/', { replace: true })
        return
      }
      setParseError(apiErrorMessage(error, 'Could not read that resume. Try a text-based PDF.'))
    } finally {
      if (aliveRef.current) {
        setParsing(false)
      }
    }
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!form) return
    // payloadOf drops rows the user left completely blank and produces the
    // exact request shape; block the save with a specific message for rows
    // missing a required field (the backend would 422).
    const payload = payloadOf(form)
    if (payload.experience.some(entry => !entry.title.trim() || !entry.company.trim())) {
      setSaveError('Each experience entry needs a job title and a company — fill them in or remove the row.')
      return
    }
    if (payload.education.some(entry => !entry.school.trim())) {
      setSaveError('Each education entry needs a school — fill them in or remove the row.')
      return
    }
    setSaving(true)
    setSaveError(null)
    try {
      const saved = await saveCandidateProfile(payload)
      if (!aliveRef.current) return
      // Zip the echo onto the surviving rows (ids preserved — see
      // zipExperienceEcho) instead of rehydrating with fresh ids, which
      // would remount and re-animate every row on each save.
      const echoed: ProfileRowsState = {
        headline: saved.headline,
        location: saved.location,
        summary: saved.summary,
        skills: saved.skills,
        experience: zipExperienceEcho(
          form.experience.filter(entry => !isBlankExperience(entry)),
          saved.experience,
        ),
        education: zipEducationEcho(
          form.education.filter(entry => !isBlankEducation(entry)),
          saved.education,
        ),
        linkedin_url: saved.linkedin_url,
        github_url: saved.github_url,
        portfolio_url: saved.portfolio_url,
      }
      setForm(echoed)
      setSavedSnapshot(echoed)
      toast({ title: 'Profile saved' })
    } catch (error) {
      if (!aliveRef.current) return
      setSaveError(apiErrorMessage(error, 'Could not save your profile.'))
    } finally {
      if (aliveRef.current) setSaving(false)
    }
  }

  return (
    <AppShell active="profile">
          <header className="screen-head">
            <div>
              <h1>Your profile</h1>
              <p>
                This powers your interviews: Aura uses your skills and resume to ask sharper questions.
                Upload your resume to autofill the form.
              </p>
            </div>
          </header>

          <form onSubmit={submit}>
            <div className="wsections">
              {/* One section at a time: the rail picks the pane, form state
                  lives at the page level — switching never loses edits. */}
              <nav className="wsection-nav" aria-label="Profile sections">
                {SECTIONS.map(s => (
                  <button
                    key={s.id}
                    type="button"
                    className={section === s.id ? 'wsection-link is-active' : 'wsection-link'}
                    aria-current={section === s.id || undefined}
                    onClick={() => showSection(s.id)}
                  >
                    {s.icon}
                    <span>{s.label}</span>
                    {isSectionDirty(s.id) && <span className="wdot" aria-hidden="true" />}
                  </button>
                ))}
              </nav>

              <div className="panel wprofile-card wsection-pane aura-panel-in" key={section}>
                <h2>{SECTIONS.find(s => s.id === section)?.label}</h2>
                {section === 'identity' && (
                  <>
                  <TextField
                    id="cp-headline"
                    label="Headline"
                    type="text"
                    value={form.headline}
                    onChange={e => set('headline', e.target.value)}
                    maxLength={200}
                  />

                  <TextField
                    id="cp-location"
                    label="Location"
                    type="text"
                    value={form.location}
                    onChange={e => set('location', e.target.value)}
                    maxLength={120}
                  />

                  <TextAreaField
                    id="cp-summary"
                    label="Summary"
                    value={form.summary}
                    onChange={e => set('summary', e.target.value)}
                    rows={4}
                    maxLength={4000}
                  />
                  </>
                )}

                {section === 'skills' && (
                  <>
                  <div className="field">
                    {/* The pane heading already says "Skills" — the label adds
                        the instruction and the live count. cleanSkills applies
                        the profile contract (trim, 60 chars, case-insensitive,
                        cap 30) at set time; a skill it drops still shows as a
                        token until the page reloads, it just never reaches the
                        save. */}
                    <label>Add your skills <span className="hint">({form.skills.length} / {MAX_SKILLS})</span></label>
                    <SkillsPicker
                      values={form.skills}
                      onChange={next => set('skills', cleanSkills(next))}
                      max={MAX_SKILLS}
                      onMaxSelected={() => toast({
                        title: 'Skill limit reached',
                        description: `At most ${MAX_SKILLS} skills fit on a profile.`,
                        variant: 'destructive',
                      })}
                    />
                  </div>
                  </>
                )}

                {section === 'identity' && (
                  <>
                  {/* Each link names itself — the old group label + bare
                      placeholders read as three anonymous strips. */}
                  <div className="links-stack">
                    <TextField
                      id="cp-linkedin"
                      label="LinkedIn profile"
                      type="url"
                      value={form.linkedin_url ?? ''}
                      onChange={e => set('linkedin_url', e.target.value || null)}
                      maxLength={512}
                    />
                    <TextField
                      id="cp-github"
                      label="GitHub profile"
                      type="url"
                      value={form.github_url ?? ''}
                      onChange={e => set('github_url', e.target.value || null)}
                      maxLength={512}
                    />
                    <TextField
                      id="cp-portfolio"
                      label="Portfolio site"
                      type="url"
                      value={form.portfolio_url ?? ''}
                      onChange={e => set('portfolio_url', e.target.value || null)}
                      maxLength={512}
                    />
                  </div>
                  </>
                )}

                {section === 'experience' && (
                  <>
                    {/* Same row enter/exit as the invite builder: rows rise in
                        on add, fade on remove, siblings glide via `layout`. */}
                    <AnimatePresence initial={false} mode="popLayout">
                    {form.experience.map((entry, idx) => (
                      <motion.div
                        key={entry.id}
                        className="wentry"
                        layout
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.2, ease: EASE_OUT }}
                      >
                        <div className="wentry-head">
                          <span className="entry-title">{entry.title || `Entry ${idx + 1}`}</span>
                          {/* The bold title already says the job — the sub only adds the company. */}
                          <span className="entry-sub">{entry.company || 'Empty'}</span>
                          <button type="button" className="wentry-remove link-action" onClick={() => removeExperience(entry.id)}>Remove</button>
                        </div>
                        <div className="field-row">
                          <TextField id={`cp-exp-${entry.id}-title`} label="Job title" type="text" value={entry.title} onChange={e => updateExperience(entry.id, { title: e.target.value })} maxLength={120} aria-label={`Job title (position ${idx + 1})`} />
                          <TextField id={`cp-exp-${entry.id}-company`} label="Company" type="text" value={entry.company} onChange={e => updateExperience(entry.id, { company: e.target.value })} maxLength={120} aria-label={`Company (position ${idx + 1})`} />
                        </div>
                        <div className="field-row">
                          <TextField id={`cp-exp-${entry.id}-start`} label="Start (e.g. 2022-03)" type="text" value={entry.start} onChange={e => updateExperience(entry.id, { start: e.target.value })} maxLength={40} aria-label={`Start date (position ${idx + 1})`} />
                          <TextField id={`cp-exp-${entry.id}-end`} label="End (or 'Present')" type="text" value={entry.end} onChange={e => updateExperience(entry.id, { end: e.target.value })} maxLength={40} aria-label={`End date (position ${idx + 1})`} />
                        </div>
                        <TextAreaField id={`cp-exp-${entry.id}-description`} label="Description" value={entry.description} onChange={e => updateExperience(entry.id, { description: e.target.value })} rows={2} maxLength={2000} aria-label={`Description (position ${idx + 1})`} />
                      </motion.div>
                    ))}
                    </AnimatePresence>
                    <button type="button" className="wbtn wbtn-secondary" onClick={addExperience}>+ Add experience</button>
                  </>
                )}

                {section === 'education' && (
                  <>
                    <AnimatePresence initial={false} mode="popLayout">
                    {form.education.map((entry, idx) => (
                      <motion.div
                        key={entry.id}
                        className="wentry"
                        layout
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.2, ease: EASE_OUT }}
                      >
                        <div className="wentry-head">
                          <span className="entry-title">{entry.school || `Entry ${idx + 1}`}</span>
                          <span className="entry-sub">{[entry.degree, entry.field].filter(Boolean).join(' · ') || 'Empty'}</span>
                          <button type="button" className="wentry-remove link-action" onClick={() => removeEducation(entry.id)}>Remove</button>
                        </div>
                        <div className="field-row">
                          <TextField id={`cp-edu-${entry.id}-school`} label="School" type="text" value={entry.school} onChange={e => updateEducation(entry.id, { school: e.target.value })} maxLength={160} aria-label={`School (education ${idx + 1})`} />
                          <TextField id={`cp-edu-${entry.id}-degree`} label="Degree" type="text" value={entry.degree} onChange={e => updateEducation(entry.id, { degree: e.target.value })} maxLength={160} aria-label={`Degree (education ${idx + 1})`} />
                        </div>
                        <div className="field-row-3">
                          <TextField id={`cp-edu-${entry.id}-field`} label="Field" type="text" value={entry.field} onChange={e => updateEducation(entry.id, { field: e.target.value })} maxLength={160} aria-label={`Field of study (education ${idx + 1})`} />
                          <TextField id={`cp-edu-${entry.id}-start`} label="Start" type="text" value={entry.start} onChange={e => updateEducation(entry.id, { start: e.target.value })} maxLength={40} aria-label={`Start year (education ${idx + 1})`} />
                          <TextField id={`cp-edu-${entry.id}-end`} label="End" type="text" value={entry.end} onChange={e => updateEducation(entry.id, { end: e.target.value })} maxLength={40} aria-label={`End year (education ${idx + 1})`} />
                        </div>
                      </motion.div>
                    ))}
                    </AnimatePresence>
                    <button type="button" className="wbtn wbtn-secondary" onClick={addEducation}>+ Add education</button>
                  </>
                )}

                {section === 'resume' && (
                  <>
                    <p className="wnote">
                      {resumeStored
                        ? 'A resume is on file — you can start interviews without re-uploading. Upload again to replace it and re-run autofill.'
                        : 'Upload your resume to autofill this profile and start interviews without re-uploading.'}
                    </p>
                    <ResumeDropzone
                      stored={resumeStored}
                      busy={parsing}
                      busyLabel="Parsing your resume…"
                      disabled={parsing}
                      title="Upload resume (PDF)"
                      description="Parsed on the spot — your skills and history land in the form below."
                      hint="max 10 MB · text-based PDF works best"
                      onReject={setParseError}
                      onFile={handleResume}
                    />
                    {parseError && <div className="wbanner is-flat" role="alert">{parseError}</div>}
                  </>
                )}
              </div>
            </div>

            {saveError && <div className="wbanner" role="alert">{saveError}</div>}

            {/* Sticky: the primary action stays reachable no matter how far
                the user has scrolled into the repeatable entries. */}
            <div className="wsave-bar">
              <Link to={user?.role === '' ? '/choose-role' : '/dashboard'} className="wbtn wbtn-secondary">Back</Link>
              <span className="wspacer" />
              <LoadingButton type="submit" size="lg" loading={saving}>
                Save profile
              </LoadingButton>
            </div>
          </form>
      <Toaster />
    </AppShell>
  )
}
